import { memo, useCallback } from "react";
import { Box, Stack, TextField, Typography, useTheme } from "@mui/material";
import PropTypes from "prop-types";

import { ButtonV1 } from "components";
import SelectAll from "./SelectAll";

import { useToaster } from "hooks";
import { isFgUpdated } from "../makeData";
import {
  FG_BULK_ACTION_MODE,
  FG_TRACKING_LABELS,
  SELECT_ALL_ELIGIBILITY_MESSAGE,
} from "../constants";

const modeButtonStyles = (theme, isActive) => ({
  borderRadius: "8px",
  border: "1px solid",
  borderColor: isActive ? theme.palette.primary.main : theme.palette.grey[300],
  backgroundColor: isActive
    ? theme.palette.primary.main
    : theme.palette.common.white,
  color: isActive ? theme.palette.common.white : theme.palette.text.primary,
  textTransform: "none",
  minWidth: 118,
  px: 1.5,
  py: 1,
  minHeight: 40,
  fontWeight: 700,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
});

const FGBulkActions = ({
  order,
  allEligibleSelected,
  isEligiblePartiallySelected,
  onToggleSelectAll,
  totalLineItemsQty,
  bulkActionMode,
  setBulkActionMode,
  dueDate,
  setDueDate,
  handleSubmitBulkUpdate,
  onCancelBulkAction,
  isDueDateMissing,
  shouldShowActionMessage,
  showExpectedFgWarning,
  effectiveSelectedEligibleCount,
  formatDialogDate,
  selectedEligibleReducedCount,
  totalEligibleQty,
}) => {
  const theme = useTheme();
  const triggerToaster = useToaster();

  const isFgReadySelected = bulkActionMode === FG_BULK_ACTION_MODE.FG_READY;
  const isExpectedFgSelected =
    bulkActionMode === FG_BULK_ACTION_MODE.EXPECTED_FG;

  const handleSelectAllChange = useCallback(() => {
    if (!isFgUpdated(order.lineItems)) {
      onToggleSelectAll(order.orderId, allEligibleSelected);
    } else {
      triggerToaster(SELECT_ALL_ELIGIBILITY_MESSAGE, "error");
    }
  }, [
    allEligibleSelected,
    onToggleSelectAll,
    order.lineItems,
    order.orderId,
    triggerToaster,
  ]);

  const handleBulkActionModeToggle = useCallback(
    (mode) => {
      setBulkActionMode(bulkActionMode === mode ? null : mode);
    },
    [bulkActionMode, setBulkActionMode],
  );

  return (
    <>
      {order.lineItems?.length > 1 && (
        <>
          {/* Header: Select all and summary */}
          <Box
            px={2}
            py={1.5}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
            >
              <SelectAll
                checked={allEligibleSelected}
                indeterminate={isEligiblePartiallySelected}
                onChange={handleSelectAllChange}
                ariaLabel={`${FG_TRACKING_LABELS.selectAll} ${order.orderId}`}
                label={FG_TRACKING_LABELS.selectAll}
              />
              <Typography variant="body2" color="primary.main" fontWeight={700}>
                {`SKUs — ${
                  order.lineItems.length
                } line items · ${totalLineItemsQty.toFixed(2)} MT total`}
              </Typography>
            </Stack>

            <Typography variant="caption" color="text.secondary">
              {FG_TRACKING_LABELS.bulkUpdateCopy}
            </Typography>
          </Box>

          {/* Bulk action controls shown only when select-all is active */}
          {allEligibleSelected && (
            <>
              <Box
                mx={2}
                mb={2}
                px={2}
                py={2}
                border="1px solid"
                borderColor="primary.main"
                borderRadius={1.5}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  justifyContent="space-between"
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="primary.main"
                    >
                      {`Mark all eligible line items as:`}
                    </Typography>
                    <Box
                      component="button"
                      type="button"
                      onClick={() =>
                        handleBulkActionModeToggle(FG_BULK_ACTION_MODE.FG_READY)
                      }
                      style={modeButtonStyles(theme, isFgReadySelected)}
                      aria-pressed={isFgReadySelected}
                    >
                      <span
                        style={{ display: "inline-flex", alignItems: "center" }}
                      >
                        <input
                          type="checkbox"
                          checked={isFgReadySelected}
                          readOnly
                          style={{ width: 14, height: 14, marginLeft: 2 }}
                        />
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 13,
                            fontWeight: 700,
                          }}
                        >
                          {FG_TRACKING_LABELS.fgReadyTag}
                        </span>
                      </span>
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={() =>
                        handleBulkActionModeToggle(
                          FG_BULK_ACTION_MODE.EXPECTED_FG,
                        )
                      }
                      style={modeButtonStyles(theme, isExpectedFgSelected)}
                      aria-pressed={isExpectedFgSelected}
                    >
                      <span
                        style={{ display: "inline-flex", alignItems: "center" }}
                      >
                        <input
                          type="checkbox"
                          checked={isExpectedFgSelected}
                          readOnly
                          style={{ width: 14, height: 14, marginLeft: 2 }}
                        />
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 13,
                            fontWeight: 700,
                          }}
                        >
                          {FG_TRACKING_LABELS.expectedFgTag}
                        </span>
                      </span>
                    </Box>

                    {bulkActionMode === FG_BULK_ACTION_MODE.EXPECTED_FG && (
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                        >
                          {FG_TRACKING_LABELS.dueDateLabel}
                        </Typography>
                        <TextField
                          size="small"
                          type="date"
                          value={dueDate}
                          onChange={(e) => setDueDate(e.target.value)}
                          sx={{
                            width: 170,
                            "& .MuiOutlinedInput-input": { py: 0.8 },
                          }}
                        />
                      </>
                    )}
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <ButtonV1
                      title={FG_TRACKING_LABELS.cancelCta}
                      variant="outlined"
                      color="secondary"
                      style={{ maxWidth: 60, height: 32 }}
                      size="medium"
                      textStyle={{ fontSize: 12 }}
                      onClick={onCancelBulkAction}
                      disabled={!bulkActionMode}
                    />
                    <ButtonV1
                      title={FG_TRACKING_LABELS.submitCta}
                      style={{ maxWidth: 60, height: 32 }}
                      disabled={
                        !bulkActionMode ||
                        (isExpectedFgSelected && isDueDateMissing)
                      }
                      onClick={handleSubmitBulkUpdate}
                      size="medium"
                      textStyle={{ fontSize: 12 }}
                    />
                  </Stack>
                </Stack>
              </Box>

              <Box px={2} pb={1.2}>
                {isDueDateMissing ? (
                  <Box
                    px={1.5}
                    py={0.9}
                    mb={2}
                    border="1px solid"
                    borderColor={theme.palette.error.dark}
                    borderRadius={1}
                    sx={{ backgroundColor: theme.palette.error.main }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.error.dark, fontWeight: 600 }}
                    >
                      Enter a due date when Expected FG is selected
                    </Typography>
                  </Box>
                ) : (
                  shouldShowActionMessage && (
                    <Typography
                      variant="body2"
                      mb={2}
                      sx={{ color: theme.palette.info.main, fontWeight: 600 }}
                    >
                      {FG_TRACKING_LABELS.infoMessageFgReadyPrefix}{" "}
                      {effectiveSelectedEligibleCount}{" "}
                      {showExpectedFgWarning
                        ? FG_TRACKING_LABELS.infoMessageExpectedMiddle
                        : FG_TRACKING_LABELS.infoMessageFgReadyMiddle}{" "}
                      ({totalEligibleQty.toFixed(2)} MT total)
                      {showExpectedFgWarning && (
                        <>
                          {" "}
                          {
                            FG_TRACKING_LABELS.infoMessageExpectedDatePrefix
                          }{" "}
                          {formatDialogDate(dueDate)}.{" "}
                          {selectedEligibleReducedCount > 0 && (
                            <Box
                              component="span"
                              sx={{ color: theme.palette.error.dark }}
                            >{`${selectedEligibleReducedCount} lineitems will be reduced — exceptions will be raised.`}</Box>
                          )}
                        </>
                      )}
                      {!showExpectedFgWarning && "."}
                    </Typography>
                  )
                )}
              </Box>
            </>
          )}
        </>
      )}
    </>
  );
};

FGBulkActions.propTypes = {
  order: PropTypes.object.isRequired,
  allEligibleSelected: PropTypes.bool.isRequired,
  isEligiblePartiallySelected: PropTypes.bool.isRequired,
  onToggleSelectAll: PropTypes.func.isRequired,
  totalLineItemsQty: PropTypes.number.isRequired,
  bulkActionMode: PropTypes.string,
  setBulkActionMode: PropTypes.func.isRequired,
  dueDate: PropTypes.string,
  setDueDate: PropTypes.func.isRequired,
  handleSubmitBulkUpdate: PropTypes.func.isRequired,
  onCancelBulkAction: PropTypes.func.isRequired,
  isDueDateMissing: PropTypes.bool,
  shouldShowActionMessage: PropTypes.bool,
  showExpectedFgWarning: PropTypes.bool,
  effectiveSelectedEligibleCount: PropTypes.number,
  formatDialogDate: PropTypes.func.isRequired,
  selectedEligibleReducedCount: PropTypes.number,
};

FGBulkActions.defaultProps = {
  bulkActionMode: null,
  dueDate: "",
  isDueDateMissing: false,
  shouldShowActionMessage: false,
  showExpectedFgWarning: false,
  effectiveSelectedEligibleCount: 0,
  selectedEligibleReducedCount: 0,
};

export default memo(FGBulkActions);
